import Datatable from "./datatable_controller";
import * as bootstrap from "bootstrap";

// Connects to data-controller="organization-order-datatable"
export default class extends Datatable {
  static targets = ['filter', 'statusFilter', 'emptyNoOrders', 'emptyNoResults',
                    'noResultsEcho', 'dateInput', 'searchInput', 'exportBtn',
                    'filterTrigger', 'eventSearch', 'eventList', 'eventEmpty', 'eventCount']
  static values = {
    exportUrl: String,
    searchDisabledTitle: String,
    filterDisabledTitle: String,
    exportDisabledTitle: String,
  }

  connect() {
    super.connect();
    $(this.filterTargets).on('select2:select select2:unselect', this.filterSelected.bind(this))
    $(this.statusFilterTargets).on('select2:select select2:unselect', this.filterSelected.bind(this))

    // Keep the picker's "N selected" footer count in sync as rows are toggled.
    this.filterTargets.forEach(cb =>
      cb.addEventListener('change', () => this.updateEventCount()))
    this.updateEventCount()

    // After every draw, pick which empty variant to show and sync the toolbar's
    // disabled state. recordsTotal is the unfiltered count, so >0 with an empty
    // page means the active filters/search matched nothing.
    if (this.dataTable) {
      this.dataTable.on('draw.dt', () => {
        this.updateEmptyVariant()
        this.updateToolbarState()
      })
    }
    // NB: we deliberately do NOT call updateToolbarState() here on connect. Before
    // the first ajax draw, dataTable.page.info().recordsTotal reads 0 even for orgs
    // that DO have orders, which would momentarily disable search/filter and flash.
    // The server-rendered HTML already reflects @has_orders correctly for first
    // paint; the draw.dt handler then takes over as the source of truth.
  }

  // Toggle the right empty state and, for no-results, echo the active filters.
  updateEmptyVariant() {
    if (!this.hasEmptyNoOrdersTarget || !this.hasEmptyNoResultsTarget) return
    const info = this.dataTable.page.info()
    const filtering = info.recordsTotal > 0       // orders exist; filters hid them
    const empty = info.recordsDisplay === 0

    this.emptyNoOrdersTarget.classList.toggle('d-none', !empty || filtering)
    this.emptyNoResultsTarget.classList.toggle('d-none', !empty || !filtering)

    if (empty && filtering && this.hasNoResultsEchoTarget) {
      this.noResultsEchoTarget.textContent = this.activeFilterSummary()
    }
  }

  // Keep the toolbar's disabled state in sync with the data.
  //
  //   true-empty   (recordsTotal === 0): the org has ZERO orders at all. Search
  //                + "Filter by event" are dead controls — they can only ever
  //                return empty — so disable them (never hide: that would reflow
  //                the layout once the first order lands). Export off too.
  //   filtered-empty (recordsTotal > 0 && recordsDisplay === 0): orders exist but
  //                the active filter/search hid them. Search + filter stay ENABLED
  //                so the user can clear/adjust; only Export is disabled.
  //
  // recordsTotal is the unfiltered count; recordsDisplay is the current view.
  updateToolbarState() {
    if (!this.dataTable) return
    const info = this.dataTable.page.info()
    const trueEmpty = info.recordsTotal === 0

    // Export: nothing to download whenever the current view has no rows.
    if (this.hasExportBtnTarget) {
      this.setDisabled(this.exportBtnTarget, info.recordsDisplay === 0,
                       this.exportDisabledTitleValue)
    }

    // Search + Filter-by-event: only dead when there are NO orders at all.
    if (this.hasSearchInputTarget) {
      this.setDisabled(this.searchInputTarget, trueEmpty, this.searchDisabledTitleValue)
    }
    if (this.hasFilterTriggerTarget) {
      this.setDisabled(this.filterTriggerTarget, trueEmpty, this.filterDisabledTitleValue)
    }
  }

  // Toggle a control's disabled state, keeping aria-disabled + the explanatory
  // title in lockstep. When re-enabling, the "available once…" title is cleared
  // so enabled controls don't keep a stale tooltip.
  setDisabled(el, disabled, title) {
    el.disabled = disabled
    if (disabled) {
      el.setAttribute('aria-disabled', 'true')
      if (title) el.setAttribute('title', title)
    } else {
      el.removeAttribute('aria-disabled')
      el.removeAttribute('title')
    }
  }


  // A short human echo of what's currently filtering, e.g.
  // “No results for “zzqq” in Summer Gala · Refunded.”
  activeFilterSummary() {
    const parts = []
    const term = (this.hasSearchInputTarget ? this.searchInputTarget.value : '').trim()
    const events = this.filterTargets.filter(f => f.checked)
      .map(f => (f.dataset.label || f.nextElementSibling?.textContent || '').trim()).filter(Boolean)
    const statuses = this.statusFilterTargets.filter(f => f.checked)
      .map(f => (f.dataset.label || f.nextElementSibling?.textContent || '').trim()).filter(Boolean)
    const scopes = [...events, ...statuses]
    if (term) parts.push(`“${term}”`)
    if (scopes.length) parts.push(scopes.join(' · '))
    return parts.length ? `No results for ${parts.join(' in ')}. ` : ''
  }

  // Export the org's orders to CSV, carrying the active event filter + search so
  // the file matches the on-screen table. Navigates to the download URL (browser
  // handles the file download from the CSV response).
  exportCsv() {
    const base = this.exportUrlValue
    if (!base) return
    if (this.hasExportBtnTarget && this.exportBtnTarget.disabled) return
    const url = new URL(base, window.location.origin)
    this.filterTargets.filter(f => f.checked).forEach(f => url.searchParams.append("event_ids[]", f.value))
    const term = (this.hasSearchInputTarget ? this.searchInputTarget.value : "").trim()
    if (term) url.searchParams.set("search", term)
    window.location.href = url.toString()
  }

  // “Clear filters” on the no-results state: reset search + every filter + date.
  clearAllFilters() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
    if (this.dataTable) this.dataTable.search('')
    this.filterTargets.forEach(cb => { cb.checked = false })
    this.statusFilterTargets.forEach(cb => { cb.checked = false })
    if (this.hasDateInputTarget) this.dateInputTarget.value = ''
    this.startDate = ''
    this.endDate = ''
    this.dataTable.draw()
  }

  filterSelected(_event) {
    this.dataTable.draw()
  }

  manipulateData(data) {
    // Collect all checked event_ids
    const eventIds = [];
    this.filterTargets.forEach((filter) => {
      if (filter.checked) eventIds.push(filter.value)
    });
    data['event_ids'] = eventIds

    // Collect all checked status_ids
    const statusIds = [];
    this.statusFilterTargets.forEach((filter) => {
      if (filter.checked) {
        statusIds.push(filter.value);
      }
    });
    data['status_ids'] = statusIds;

    data['start_date'] = this.startDate;
    data['end_date'] = this.endDate;

    return data;
  }

  applyEventFilter() {
    this.dataTable.draw();
    this.closeEventFilter();
  }

  // "Clear" (header) resets the selection but keeps the picker open so the user
  // can re-pick. "Cancel" (footer) dismisses the picker.
  removeEventFilter() {
    this.filterTargets.forEach(cb => cb.checked = false);
    this.updateEventCount();
    this.dataTable.draw();
  }

  cancelEventFilter() {
    this.closeEventFilter();
  }

  // The picker uses data-bs-auto-close="outside", so clicks on its own
  // Apply/Cancel buttons don't dismiss it — close it explicitly.
  closeEventFilter() {
    if (!this.hasFilterTriggerTarget) return;
    bootstrap.Dropdown.getOrCreateInstance(this.filterTriggerTarget).hide();
  }

  // Live client-side search inside the "Filter by event" picker: show only the
  // event rows whose title contains the typed term, and reveal a "no matches"
  // state when the term hides every row (style guide §10b behavior).
  filterEventRows() {
    if (!this.hasEventSearchTarget) return;
    const term = this.eventSearchTarget.value.trim().toLowerCase();
    let visible = 0;

    this.eventListTarget.querySelectorAll('.ep-row').forEach((row) => {
      const name = row.dataset.eventName || '';
      const match = name.includes(term);
      row.classList.toggle('d-none', !match);
      if (match) visible++;
    });

    if (this.hasEventEmptyTarget) {
      this.eventEmptyTarget.classList.toggle('d-none', visible > 0);
    }
  }

  // Footer "N selected" count — pluralized via the data-* labels the view sets.
  updateEventCount() {
    if (!this.hasEventCountTarget) return;
    const n = this.filterTargets.filter(cb => cb.checked).length;
    const tpl = n === 1
      ? this.eventCountTarget.dataset.one
      : this.eventCountTarget.dataset.other;
    if (tpl) this.eventCountTarget.textContent = tpl.replace('__N__', n);
  }

  applyStatusFilter() {
    this.dataTable.draw();
  }

  removeStatusFilter() {
    this.statusFilterTargets.forEach(cb => cb.checked = false);
    this.dataTable.draw();
  }

  get columns() {
    return [
      {data: "date"},
      {data: "order_id"},
      {data: "buyer"},
      {data: "event"},
      {data: "tickets"},
      {data: "status"},
      {data: "amount"},
      {data: "actions"},
    ];
  }

  get searching() {
    return true;
  }

  get ordering() {
    return false;
  }

  get lengthChange() {
    return false;
  }
}
